/**
 * System status → Overview: alerts, health at a glance, job totals and library figures. 1.2.2: each alert can be
 * suppressed (Suppress…, with an optional note; an error asks first); it then stays hidden until someone unsuppresses
 * it, and Show suppressed (n) lists them, whether each is still occurring, by whom, when and why.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, AlertTriangle, BellOff, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { api, errorMessage } from '../../../api/client';
import { queryKeys } from '../../../api/queryKeys';
import { Modal } from '../../../components/Modal';
import { TableScroll } from '../../../components/TableScroll';
import { toast } from '../../../components/Toasts';
import { ACTIVE_JOB_STATUSES } from '../../../components/Status';
import { bytes, dateTime, relative } from '../../../lib/format';
import { Dot, type AlertSuppressionDTO, type Section, type StatusAlertDTO, type StatusDTO } from './kit';

/** Suppress…: an optional note; an error says what suppressing it means before it is hidden. */
function SuppressDialog({ alert, onClose }: { alert: StatusAlertDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const error = alert.level === 'error';
  const save = async () => {
    setBusy(true);
    try {
      await api('/api/v1/system/alerts/suppress', { method: 'POST', json: { key: alert.key, ...(note.trim() ? { note: note.trim() } : {}) } });
      toast('Suppressed until someone unsuppresses it');
      await Promise.all([qc.invalidateQueries({ queryKey: ['system-status'] }), qc.invalidateQueries({ queryKey: queryKeys.alertSummary() })]);
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  };
  return (
    <Modal title={error ? 'Suppress this error?' : 'Suppress this warning?'} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className={`btn ${error ? 'danger' : 'primary'}`} disabled={busy} onClick={() => void save()} data-testid="suppress-confirm"><BellOff size={14} /> {error ? 'Suppress the error' : 'Suppress'}</button>
    </>}>
      <p className={`banner ${error ? 'error' : 'warning'}`} style={{ marginTop: 0 }} data-testid="suppress-message">{alert.message}</p>
      <p className="muted" style={{ marginTop: 0 }} data-testid="suppress-rule">
        {error ? <><b>This hides an error until someone unsuppresses it.</b> It is not emailed either, even if it gets worse in the same way. </> : 'It stays hidden, and is not emailed, until someone unsuppresses it, even when it clears and comes back. '}
        Suppressed warnings stay listed under Show suppressed.
      </p>
      <div className="field">
        <label htmlFor="suppress-note">Note (optional)</label>
        <textarea id="suppress-note" className="input" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Why, for example: the three locations share the iMac’s disk; known" data-testid="suppress-note" />
      </div>
    </Modal>
  );
}

/** Show suppressed (n): every suppression, still occurring or not, with Unsuppress. */
function SuppressedList({ items }: { items: AlertSuppressionDTO[] }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const unsuppress = async (s: AlertSuppressionDTO) => {
    setBusy(s.key);
    try {
      await api(`/api/v1/system/alerts/suppressions/${encodeURIComponent(s.key)}`, { method: 'DELETE' });
      toast(s.active ? 'Unsuppressed; it shows again' : 'Unsuppressed');
      await Promise.all([qc.invalidateQueries({ queryKey: ['system-status'] }), qc.invalidateQueries({ queryKey: queryKeys.alertSummary() })]);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  };
  if (!items.length) return null;
  return (
    <div data-testid="suppressed-alerts">
      <button className="btn ghost sm" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="show-suppressed">
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {open ? 'Hide suppressed' : 'Show suppressed'} ({items.length})
      </button>
      {open && (
        <TableScroll>
          <table className="table compact" style={{ marginTop: 6 }}>
            <thead><tr><th>Warning</th><th>Now</th><th>Suppressed</th><th>Note</th><th /></tr></thead>
            <tbody>{items.map((s) => (
              <tr key={s.key} data-testid="suppressed-row" data-key={s.key} data-active={s.active}>
                <td className="wrap">{s.level === 'error' ? <AlertOctagon size={12} color="var(--danger)" style={{ verticalAlign: -1 }} /> : <AlertTriangle size={12} color="var(--warning)" style={{ verticalAlign: -1 }} />} {s.message}</td>
                <td className="nowrap">{s.active ? <span className="pill warning" data-testid="suppressed-active">Still occurring</span> : <span className="pill" data-testid="suppressed-inactive">Not occurring now</span>}</td>
                <td className="nowrap" title={dateTime(s.suppressedAt)}>{s.suppressedBy?.displayName ?? 'Someone'} · {relative(s.suppressedAt)}</td>
                <td className="wrap">{s.note ?? <span className="faint">—</span>}</td>
                <td className="actions"><div><button className="btn sm" disabled={busy === s.key} onClick={() => void unsuppress(s)} data-testid="unsuppress">Unsuppress</button></div></td>
              </tr>
            ))}</tbody>
          </table>
        </TableScroll>
      )}
    </div>
  );
}

/** An alert's action (1.0.0-rc.2), for example Disable on the demo accounts alert. */
function AlertAction({ action }: { action: { id: string; label: string } }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api<{ disabled?: string[]; retried?: number }>(`/api/v1/system/alerts/${action.id}`, { method: 'POST' });
      toast(r.disabled ? `Disabled ${r.disabled.length} demo account${r.disabled.length === 1 ? '' : 's'}` : r.retried !== undefined ? `Retrying ${r.retried} event${r.retried === 1 ? '' : 's'}` : 'Done');
      await qc.invalidateQueries({ queryKey: ['system-status'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  return <button className="btn sm" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => void run()} data-testid={`alert-action-${action.id}`}>{action.label}</button>;
}

export function Overview({ s, go }: { s: StatusDTO; go: (s: Section) => void }) {
  const [suppressing, setSuppressing] = useState<StatusAlertDTO | null>(null);
  const healthyServices = s.services.filter((x) => x.healthy).length;
  const pools = s.workers.filter((w) => w.role !== 'graphics');
  const workersOk = pools.every((w) => w.healthy);
  const replicas = pools.reduce((n, w) => n + w.replicas.filter((r) => r.healthy).length, 0);
  const busy = pools.reduce((n, w) => n + w.busy, 0);
  const offline = s.storage.filter((l) => l.enabled && l.availability === 'offline').length;
  const agentsDown = s.agents.filter((a) => a.status === 'active' && !a.connected).length;
  const jobTotals = { active: 0, done: 0, failed: 0 };
  for (const c of Object.values(s.jobs)) {
    for (const [k, v] of Object.entries(c)) {
      if (k === 'listening') continue; // a started live source waiting for a stream is not a job in progress
      if (ACTIVE_JOB_STATUSES.includes(k)) jobTotals.active += v; else if (k === 'failed' || k === 'partial') jobTotals.failed += v; else jobTotals.done += v;
    }
  }
  const suppressed = s.suppressed ?? [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1100 }}>
      {s.alerts.length === 0 ? (
        <div className="banner info" data-testid="all-clear"><div className="banner-body"><strong><CheckCircle2 size={14} style={{ verticalAlign: -2 }} /> Everything is running</strong>
          <span>Services, workers, storage and agents report no problems{suppressed.some((x) => x.active) ? ' apart from suppressed warnings' : ''}.</span></div></div>
      ) : s.alerts.map((a, i) => (
        <div key={a.key ?? i} className={`banner ${a.level === 'error' ? 'error' : 'warning'}`} data-testid="status-alert" data-key={a.key}><div className="banner-body">
          <span>{a.level === 'error' ? <AlertOctagon size={13} style={{ verticalAlign: -2 }} /> : <AlertTriangle size={13} style={{ verticalAlign: -2 }} />} {a.message}</span>
          {(a.action || a.key) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {a.action && <AlertAction action={a.action} />}
              {a.key && <button className="btn ghost sm" onClick={() => setSuppressing(a)} data-testid="alert-suppress" title="Hide this until someone unsuppresses it"><BellOff size={13} /> Suppress…</button>}
            </div>
          )}
        </div></div>
      ))}
      <SuppressedList items={suppressed} />
      {suppressing && <SuppressDialog alert={suppressing} onClose={() => setSuppressing(null)} />}
      <div className="cards">
        <section className="panel-card">
          <p className="section-label">Health</p>
          <dl className="kv">
            <dt>Services</dt><dd><button className="linkish" onClick={() => go('services')}><Dot ok={healthyServices === s.services.length} warn label={`${healthyServices} of ${s.services.length} reporting`} /></button></dd>
            <dt>Workers</dt><dd><button className="linkish" onClick={() => go('workers')}><Dot ok={workersOk} warn label={workersOk ? `${replicas} replica${replicas === 1 ? '' : 's'} · ${busy} job${busy === 1 ? '' : 's'} running` : 'Missing for some work'} /></button></dd>
            <dt>Database</dt><dd><Dot ok={s.database.ok} label={`${s.database.latencyMs} ms · ${bytes(s.database.sizeBytes)}`} /></dd>
            <dt>Event relay</dt><dd><Dot ok={s.outbox.pending === 0 || s.outbox.oldest < 60} warn label={s.outbox.pending ? `${s.outbox.pending} waiting` : 'Up to date'} /></dd>
            <dt>Storage</dt><dd><Dot ok={offline === 0} label={offline ? `${offline} offline` : `${s.storage.length} online`} /></dd>
            <dt>Storage agents</dt><dd>{s.agents.length ? <Dot ok={agentsDown === 0} warn label={agentsDown ? `${agentsDown} disconnected` : `${s.agents.length} connected`} /> : <span className="muted">None</span>}</dd>
            <dt>Transcoder</dt><dd>{!s.transcoder.configured ? <span className="muted">Not configured</span> : <Dot ok={Boolean(s.transcoder.reachable)} warn label={s.transcoder.reachable ? `${s.transcoder.info?.engine ?? 'Reachable'}${s.transcoder.info?.simulated ? ' (simulator)' : ''}` : 'Unreachable'} />}</dd>
            <dt>Speech</dt><dd>{!s.speech.configured ? <span className="muted">Not configured</span> : <Dot ok={Boolean(s.speech.reachable)} warn label={s.speech.reachable ? 'Reachable (Whisper)' : 'Unreachable'} />}</dd>
          </dl>
        </section>
        <section className="panel-card">
          <p className="section-label">Jobs (active and last 24 h)</p>
          <dl className="kv">
            <dt>Active</dt><dd className="mono">{jobTotals.active}</dd>
            <dt>Finished</dt><dd className="mono">{jobTotals.done}</dd>
            <dt>Failed</dt><dd className="mono" style={{ color: jobTotals.failed ? 'var(--danger)' : undefined }}>{jobTotals.failed}</dd>
            <dt>Errors logged</dt><dd className="mono">{Object.values(s.errors24h).reduce((a, b) => a + b, 0)}</dd>
          </dl>
          <button className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => go('jobs')}>Open jobs</button>
        </section>
        <section className="panel-card">
          <p className="section-label">Library</p>
          <dl className="kv">
            <dt>Assets</dt><dd className="mono">{s.figures.assets.toLocaleString()}</dd>
            <dt>Media</dt><dd className="mono">{s.figures.hoursOfMedia >= 1 ? `${s.figures.hoursOfMedia} h` : `${Math.round(s.figures.hoursOfMedia * 60)} min`}</dd>
            <dt>Recycle bin</dt><dd className="mono">{s.figures.inRecycleBin}</dd>
            <dt>Sequences</dt><dd className="mono">{s.figures.sequences}</dd>
            <dt>Workflows on</dt><dd className="mono">{s.figures.workflows}</dd>
            <dt>Watch folders</dt><dd className="mono">{s.figures.watchFolders}</dd>
            <dt>People</dt><dd className="mono">{s.figures.members} members · {s.figures.signedIn} signed in</dd>
          </dl>
        </section>
      </div>
    </div>
  );
}
